/**
 * Capacitor native bridge — runs only when the app is wrapped in iOS/Android shell.
 *
 * Provides helpers to:
 *   - Detect native runtime (`isNative()`)
 *   - Register for native push notifications (FCM on Android, APNs on iOS) and
 *     forward the device token to the EMA Kurer backend.
 *   - Open the booking that a notification points to.
 */
import { Capacitor } from "@capacitor/core";
import { driverApi } from "./api";

export const isNative = () => Capacitor.isNativePlatform();
export const platform = () => Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

let _registered = false;

/**
 * Initialize push notifications for the native shell.
 * Idempotent: safe to call multiple times.
 *
 * Returns:
 *   - { ok:true, token } when registration succeeded
 *   - { ok:false, reason } otherwise
 */
export async function initNativePush(getDriverAuthToken) {
  if (!isNative() || _registered) return { ok: false, reason: "not-native-or-already" };
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");

    // 1. Permission
    const perm = await PushNotifications.checkPermissions();
    let receive = perm.receive;
    if (receive === "prompt" || receive === "prompt-with-rationale") {
      const req = await PushNotifications.requestPermissions();
      receive = req.receive;
    }
    if (receive !== "granted") return { ok: false, reason: "denied" };

    // 2. Register with FCM/APNs
    await PushNotifications.register();

    // 3. Listen for the token
    const tokenPromise = new Promise((resolve, reject) => {
      const t = setTimeout(() => reject(new Error("Push registration timeout")), 15000);
      PushNotifications.addListener("registration", (token) => {
        clearTimeout(t);
        resolve(token.value);
      });
      PushNotifications.addListener("registrationError", (err) => {
        clearTimeout(t);
        reject(err);
      });
    });
    const token = await tokenPromise;

    // 4. Send token to backend (must happen AFTER driver login)
    if (getDriverAuthToken && getDriverAuthToken()) {
      await driverApi().post("/driver/push/native-register", {
        token,
        platform: platform(),
      });
    } else {
      // Cache it for after-login flow
      try { localStorage.setItem("ema-pending-native-token", JSON.stringify({ token, platform: platform() })); } catch (e) { console.warn("Cache native token failed:", e?.message); }
    }

    // 5. When a notification is tapped → navigate to /driver (or specific booking)
    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const data = action.notification?.data || {};
      const bookingId = data.booking_id;
      if (bookingId) window.location.href = `/driver?booking=${bookingId}`;
      else window.location.href = "/driver";
    });

    _registered = true;
    return { ok: true, token };
  } catch (err) {
    console.error("initNativePush failed:", err);
    return { ok: false, reason: err?.message || String(err) };
  }
}

/**
 * Flush any pending native push token that was captured before the user logged in.
 * Call this right after a successful driver login.
 */
export async function flushPendingNativeToken() {
  if (!isNative()) return;
  try {
    const raw = localStorage.getItem("ema-pending-native-token");
    if (!raw) return;
    const { token, platform: plt } = JSON.parse(raw);
    await driverApi().post("/driver/push/native-register", { token, platform: plt });
    localStorage.removeItem("ema-pending-native-token");
  } catch (e) {
    console.warn("flushPendingNativeToken failed:", e);
  }
}
